import React, { useState, useEffect, useRef } from 'react';
import { TextField, CircularProgress, Box, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import { getCombosJWT } from '../../../api/endpoints';

const BuscadorPersonas = ({ value, onChange, label, placeholder }) => {
    const [opciones, setOpciones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [open, setOpen] = useState(false);
    const timeoutRef = useRef(null);

    // Sincronizar inputValue con value externo
    useEffect(() => {
        if (typeof value === 'string') setInputValue(value || '');
        else if (!value) setInputValue('');
    }, [value]);

    useEffect(() => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        if ((inputValue || '').trim().length < 3) {
            setOpciones([]);
            setOpen(false);
            return;
        }

        timeoutRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const res = await getCombosJWT('personal', inputValue, 30);
                const items = res?.items || [];
                // Mapear {id, label} a formato compatible
                const mapped = items.map(i => ({
                    id: i.id,
                    nombre: i.label || '',
                    Nombre: i.label || '',
                    matricula: i.matricula || '',
                    cuil: i.cuil || '',
                    domicilio: i.domicilio || '',
                }));
                setOpciones(mapped);
                setOpen(mapped.length > 0);
            } catch (err) {
                console.error('Error al buscar:', err);
                setOpciones([]);
                setOpen(false);
            } finally {
                setLoading(false);
            }
        }, 300);

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [inputValue]);

    return (
        <Autocomplete
            open={open}
            onOpen={() => { if (opciones.length > 0) setOpen(true); }}
            onClose={() => setOpen(false)}

            options={opciones}
            loading={loading}

            // Evitar filtrado local (clave para números como DNI/CUIT)
            filterOptions={(opts) => opts}

            // Cómo “etiquetar” cada opción
            getOptionLabel={(option) => {
                if (!option) return '';
                if (typeof option === 'string') return option;
                return option.nombre || option.Nombre || ''; // tolerante al backend
            }}

            // Cómo detectar selección (v4)
            isOptionEqualToValue={(opt, val) => (opt?.id || opt?.Id) === (val?.id || val?.Id)}

            inputValue={inputValue}
            onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input') {
                    setInputValue(newInputValue);
                } else if (reason === 'clear') {
                    setInputValue('');
                    onChange('', null);
                    setOpciones([]);
                    setOpen(false);
                } else if (reason === 'reset') {
                    // ignorar
                }
            }}

            onChange={(event, newValue) => {
                if (newValue && typeof newValue === 'object') {
                    const nombre = newValue.nombre || newValue.Nombre || '';
                    const id = newValue.id || newValue.Id || null;
                    onChange(nombre, id);
                    setInputValue(nombre);
                    setOpen(false);
                    setOpciones([]);
                } else if (typeof newValue === 'string') {
                    onChange(newValue, null);
                } else {
                    onChange('', null);
                    setInputValue('');
                }
            }}

            renderOption={(props, option) => {
                const { key, ...rest } = props;
                return (
                    <li key={key} {...rest}>
                        <Box>
                            <Typography variant="body2" style={{ fontWeight: 500 }}>
                                {option.nombre || option.Nombre}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {option.matricula ? `Mat: ${option.matricula}` : ''}
                                {(option.cuil || option.CUIL || option.cuit || option.CUIT) ? `${option.matricula ? ' • ' : ''}${option.cuil || option.CUIL || option.cuit || option.CUIT}` : ''}
                                {(option.tipo || option.Tipo) ? `${(option.matricula || option.cuil || option.CUIL) ? ' • ' : ''}${option.tipo || option.Tipo}` : ''}
                            </Typography>
                        </Box>
                    </li>
                );
            }}

            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    variant="outlined"
                    placeholder={placeholder}
                    fullWidth
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}

            loadingText={
                <Box display="flex" alignItems="center" p={2}>
                    <CircularProgress size={20} style={{ marginRight: 8 }} />
                    <Typography variant="body2">Buscando...</Typography>
                </Box>
            }

            noOptionsText={
                (inputValue || '').trim().length < 3 ? (
                    <Box display="flex" alignItems="center" p={2}>
                        <InfoIcon style={{ marginRight: 8, color: '#1976d2' }} />
                        <Typography variant="body2">Escribe al menos 3 caracteres</Typography>
                    </Box>
                ) : (
                    <Box display="flex" alignItems="center" p={2}>
                        <SearchIcon style={{ marginRight: 8, color: '#999' }} />
                        <Typography variant="body2" color="text.secondary">No se encontraron resultados</Typography>
                    </Box>
                )
            }
        />
    );
};

export default BuscadorPersonas;
